import { useEffect, useMemo, useState } from 'react'
import {Search, MapPin, Briefcase, Building2, Clock, X, Filter, Bookmark, BookmarkCheck, Star, ChevronDown, ChevronUp, Sparkles, } from 'lucide-react'
import ColaboradorLayout from '@/components/ColaboradorLayout'
import { useVacancies, type Vacancy } from '@/hooks/useVacancies'
import { useAuth } from '@/hooks/useAuth'
import { lumi } from '@/lib/lumi'
import { INDUSTRIES } from '@/lib/industries'

const MODALITIES = ['Todas', 'Presencial', 'Remota', 'Hibrida']

const toTitleCase = (s: string) =>
  s.trim().toLowerCase().replace(/\s+/g, ' ').split(' ')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ')

const fmtSalary = (v: Vacancy) => {
  if (v.salaryMin && v.salaryMax)
    return `$${v.salaryMin.toLocaleString()} – $${v.salaryMax.toLocaleString()}`
  if (v.salaryMin) return `Desde $${v.salaryMin.toLocaleString()}`
  return 'A convenir'
}

// ─── Pill de modalidad ───────────────────────────────────────────────────────
function ModalityBadge({ value }: { value?: string }) {
  const map: Record<string, string> = {
    Presencial: 'bg-blue-50 text-blue-700',
    Remota: 'bg-emerald-50 text-emerald-700',
    Hibrida: 'bg-violet-50 text-violet-700',
  }
  const cls = map[value || ''] ?? 'bg-slate-100 text-slate-600'
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}>
      {value === 'Hibrida' ? 'Híbrida' : value || '—'}
    </span>
  )
}

// ─── Tarjeta de vacante ──────────────────────────────────────────────────────
interface CardProps {
  v: Vacancy
  applied: boolean
  saved: boolean
  recommended: boolean
  onSelect: () => void
  onSave: () => void
}
function VacancyCard({ v, applied, saved, recommended, onSelect, onSave }: CardProps) {
  return (
    <div className="relative rounded-xl border border-slate-200 bg-white p-5 transition hover:border-amber-300 hover:shadow-sm">
      {recommended && (
        <span className="absolute right-4 top-4 flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-600">
          <Sparkles size={11} /> Recomendada
        </span>
      )}
      <button className="w-full text-left" onClick={onSelect}>
        <div className="flex items-start justify-between gap-10 pr-6">
          <div className="min-w-0">
            <h3 className="font-bold text-slate-900 leading-snug">{v.title}</h3>
            <p className="mt-0.5 flex items-center gap-1.5 text-sm text-slate-500">
              <Building2 size={13} /> {v.companyName || 'Empresa'}
            </p>
          </div>
          {applied && (
            <span className="shrink-0 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
              Postulado
            </span>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><MapPin size={12} /> {v.city || '—'}{v.state ? `, ${v.state}` : ''}</span>
          <span className="inline-flex items-center gap-1 font-semibold text-amber-600">
            <Briefcase size={12} /> {fmtSalary(v)}
          </span>
          <ModalityBadge value={v.modality} />
          {v.industry && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{v.industry}</span>
          )}
        </div>
      </button>
      {/* Guardar */}
      <button
        onClick={onSave}
        title={saved ? 'Quitar de guardados' : 'Guardar vacante'}
        className="absolute bottom-4 right-4 text-slate-400 hover:text-amber-500 transition-colors"
      >
        {saved ? <BookmarkCheck size={18} className="text-amber-500" /> : <Bookmark size={18} />}
      </button>
    </div>
  )
}

// ─── Panel de detalle ────────────────────────────────────────────────────────
interface DetailProps {
  v: Vacancy
  applied: boolean
  applying: boolean
  onApply: () => void
  onClose: () => void
}
function VacancyDetail({ v, applied, applying, onApply, onClose }: DetailProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/50"
      onClick={onClose}
    >
      <div
        className="flex h-full w-full max-w-lg flex-col bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <h2 className="text-lg font-bold text-slate-900">Detalle de vacante</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div>
            <h3 className="text-xl font-bold text-slate-900">{v.title}</h3>
            <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500">
              <Building2 size={14} /> {v.companyName || 'Empresa'} · {v.area || '—'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <InfoBox label="Ubicación" value={`${v.city || '—'}${v.state ? `, ${v.state}` : ''}`} />
            <InfoBox label="Modalidad" value={v.modality === 'Hibrida' ? 'Híbrida' : v.modality || '—'} />
            <InfoBox label="Salario" value={fmtSalary(v)} />
            <InfoBox label="Contratación" value={v.contractType || '—'} />
            <InfoBox label="Horario" value={v.schedule || '—'} />
            <InfoBox label="Experiencia" value={v.experience || '—'} />
            {v.education && <InfoBox label="Escolaridad" value={v.education} />}
            {v.industry && <InfoBox label="Giro" value={v.industry} />}
          </div>

          {v.skills && v.skills.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-semibold text-slate-600">Habilidades requeridas</p>
              <div className="flex flex-wrap gap-2">
                {v.skills.map((s) => (
                  <span key={s} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                    {toTitleCase(s)}
                  </span>
                ))}
              </div>
            </div>
          )}

          <DetailSection title="Actividades a desempeñar" value={v.activities} />
          <DetailSection title="Requisitos" value={v.requirements} />
          <DetailSection title="Prestaciones" value={v.benefits} />
        </div>

        {/* Footer */}
        <div className="border-t border-slate-100 px-6 py-4">
          <button
            onClick={onApply}
            disabled={applying || applied}
            className="w-full rounded-lg bg-slate-900 px-5 py-3 text-sm font-semibold text-amber-400 transition hover:bg-slate-800 disabled:opacity-60"
          >
            {applied ? '✓ Ya te postulaste' : applying ? 'Enviando...' : 'Postularme a esta vacante'}
          </button>
        </div>
      </div>
    </div>
  )
}

function InfoBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-slate-50 p-3">
      <p className="text-xs text-slate-400">{label}</p>
      <p className="mt-0.5 font-semibold text-slate-800 text-sm">{value}</p>
    </div>
  )
}

function DetailSection({ title, value }: { title: string; value?: string }) {
  if (!value) return null
  return (
    <div>
      <p className="mb-1 text-xs font-semibold text-slate-600">{title}</p>
      <p className="whitespace-pre-line text-sm leading-relaxed text-slate-600">{value}</p>
    </div>
  )
}

// ─── Página principal ────────────────────────────────────────────────────────
const SAVED_KEY = 'beeznz_saved_vacancies'

function ColaboradorEmpleos() {
  const { vacancies, loading, fetchPublic } = useVacancies()
  const { user } = useAuth()

  // Filtros
  const [query, setQuery] = useState('')
  const [city, setCity] = useState('')
  const [modality, setModality] = useState('Todas')
  const [industry, setIndustry] = useState('')
  const [minSalary, setMinSalary] = useState('')
  const [showFilters, setShowFilters] = useState(false)

  // Estado
  const [selected, setSelected] = useState<Vacancy | null>(null)
  const [applying, setApplying] = useState(false)
  const [appliedIds, setAppliedIds] = useState<string[]>([])
  const [savedIds, setSavedIds] = useState<string[]>([])
  const [activeTab, setActiveTab] = useState<'todas' | 'guardadas' | 'recomendadas'>('todas')
  const [collaboratorProfile, setCollaboratorProfile] = useState<any>(null)

  // ── Cargar datos iniciales ──────────────────────────────────────────────
  useEffect(() => {
    fetchPublic()
  }, [])

  useEffect(() => {
    if (!user) return
    // Postulaciones previas
    lumi.entities.applications
      .list({ filter: { collaboratorUserId: user.userId } })
      .then((r) => setAppliedIds(r.list.map((a: any) => a.vacancyId)))
      .catch((e) => console.error('Error al cargar postulaciones:', e))
    // Perfil del colaborador para recomendaciones
    lumi.entities.collaborators
      .list({ filter: { userId: user.userId }, limit: 1 })
      .then((r) => setCollaboratorProfile(r.list[0] || null))
      .catch(() => {})
  }, [user])

  useEffect(() => {
    const stored = localStorage.getItem(SAVED_KEY)
    if (stored) {
      try { setSavedIds(JSON.parse(stored)) } catch {}
    }
  }, [])

  // ── Recomendaciones: vacantes cuyas skills coinciden con el perfil ───────
  const recommendedIds = useMemo(() => {
    if (!collaboratorProfile) return new Set<string>()
    const profileSkills: string[] = (collaboratorProfile.skills || []).map((s: string) =>
      s.toLowerCase().trim()
    )
    const profileIndustry: string = (collaboratorProfile.industry || '').toLowerCase()
    if (profileSkills.length === 0 && !profileIndustry) return new Set<string>()
    return new Set(
      vacancies
        .filter((v) => {
          const skillMatch = (v.skills || []).some((s) =>
            profileSkills.includes(s.toLowerCase().trim())
          )
          const industryMatch =
            profileIndustry && v.industry?.toLowerCase() === profileIndustry
          return skillMatch || industryMatch
        })
        .map((v) => v._id)
    )
  }, [vacancies, collaboratorProfile])

  // ── Industrias dinámicas ────────────────────────────────────────────────
  const industries = useMemo(() => {
    const set = new Set<string>(INDUSTRIES.filter((i) => i !== 'Otro'))
    vacancies.forEach((v) => v.industry && set.add(v.industry))
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'))
  }, [vacancies])

  // ── Filtrado ────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return vacancies.filter((v) => {
      if (query) {
        const q = query.toLowerCase()
        const match =
          v.title?.toLowerCase().includes(q) ||
          v.companyName?.toLowerCase().includes(q) ||
          v.area?.toLowerCase().includes(q) ||
          v.city?.toLowerCase().includes(q)
        if (!match) return false
      }
      if (city && !v.city?.toLowerCase().includes(city.toLowerCase())) return false
      if (modality !== 'Todas' && v.modality !== modality) return false
      if (industry && v.industry !== industry) return false
      if (minSalary && (v.salaryMax || v.salaryMin || 0) < Number(minSalary)) return false
      return true
    })
  }, [vacancies, query, city, modality, industry, minSalary])

  const displayed = useMemo(() => {
    if (activeTab === 'guardadas') return filtered.filter((v) => savedIds.includes(v._id))
    if (activeTab === 'recomendadas') return filtered.filter((v) => recommendedIds.has(v._id))
    return filtered
  }, [filtered, activeTab, savedIds, recommendedIds])

  // ── Acciones ────────────────────────────────────────────────────────────
  const handleSave = (id: string) => {
    setSavedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      localStorage.setItem(SAVED_KEY, JSON.stringify(next))
      return next
    })
  }

  const handleApply = async (v: Vacancy) => {
    if (!user) return
    if (appliedIds.includes(v._id)) return
    setApplying(true)
    try {
      const now = new Date().toISOString()
      await lumi.entities.applications.create({
        vacancyId: v._id,
        vacancyTitle: v.title,
        companyUserId: v.companyUserId,
        companyName: v.companyName,
        collaboratorUserId: user.userId,
        collaboratorName: collaboratorProfile?.fullName || user.email,
        collaboratorEmail: user.email,
        cvUrl: collaboratorProfile?.cvUrl || '',
        coverNote: '',
        status: 'pendiente',
        creator: user.userId,
        createdAt: now,
        updatedAt: now,
      })
      await lumi.entities.vacancies.update(v._id, {
        applicantsCount: (v.applicantsCount || 0) + 1,
        updatedAt: new Date().toISOString(),
      })
      setAppliedIds((p) => [...p, v._id])
      setSelected(null)
      alert('¡Postulación enviada con éxito!')
    } catch (e) {
      console.error('Error al postularse:', e)
      alert('Ocurrió un error al postularte. Inténtalo de nuevo.')
    } finally {
      setApplying(false)
    }
  }

  // ── Estilos reutilizables ───────────────────────────────────────────────
  const inputCls =
    'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-amber-400 focus:ring-1 focus:ring-amber-400'

  const tabCls = (t: typeof activeTab) =>
    `px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
      activeTab === t
        ? 'bg-slate-900 text-amber-400'
        : 'text-slate-600 hover:bg-slate-100'
    }`

  return (
    <ColaboradorLayout
      title="Buscar empleo"
      subtitle="Explora vacantes publicadas en Beeznz Work y postúlate en segundos"
    >
      {/* ── Barra de búsqueda ── */}
      <div className="mb-5 flex gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Puesto, empresa, área o ciudad..."
            className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-sm text-slate-900 outline-none transition focus:border-amber-400 focus:ring-1 focus:ring-amber-400"
          />
        </div>
        <button
          onClick={() => setShowFilters((v) => !v)}
          className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:border-amber-300 hover:bg-amber-50"
        >
          <Filter size={15} />
          Filtros
          {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {/* ── Panel de filtros colapsable ── */}
      {showFilters && (
        <div className="mb-5 grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-4">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">Ciudad</label>
            <input className={inputCls} value={city} onChange={(e) => setCity(e.target.value)} placeholder="Ej. Puebla" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">Modalidad</label>
            <select className={inputCls} value={modality} onChange={(e) => setModality(e.target.value)}>
              {MODALITIES.map((m) => (
                <option key={m} value={m}>{m === 'Hibrida' ? 'Híbrida' : m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">Giro</label>
            <select className={inputCls} value={industry} onChange={(e) => setIndustry(e.target.value)}>
              <option value="">Todos los giros</option>
              {industries.map((i) => (
                <option key={i} value={i}>{i}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">Salario mínimo</label>
            <input type="number" className={inputCls} value={minSalary} onChange={(e) => setMinSalary(e.target.value)} placeholder="$" />
          </div>
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="mb-5 flex items-center gap-2">
        <button className={tabCls('todas')} onClick={() => setActiveTab('todas')}>
          Todas ({filtered.length})
        </button>
        <button className={tabCls('recomendadas')} onClick={() => setActiveTab('recomendadas')}>
          <span className="flex items-center gap-1">
            <Sparkles size={13} />
            Recomendadas ({filtered.filter((v) => recommendedIds.has(v._id)).length})
          </span>
        </button>
        <button className={tabCls('guardadas')} onClick={() => setActiveTab('guardadas')}>
          <span className="flex items-center gap-1">
            <Bookmark size={13} />
            Guardadas ({savedIds.length})
          </span>
        </button>
      </div>

      {/* ── Resultados ── */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-amber-400 border-t-transparent" />
        </div>
      ) : displayed.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center">
          <Briefcase size={28} className="mx-auto text-slate-300" />
          <p className="mt-3 font-semibold text-slate-700">
            {activeTab === 'guardadas'
              ? 'No tienes vacantes guardadas'
              : activeTab === 'recomendadas'
              ? 'Completa tu perfil para recibir recomendaciones'
              : 'No hay vacantes con estos filtros'}
          </p>
          <p className="mt-1 text-sm text-slate-500">
            {activeTab === 'guardadas'
              ? 'Guarda vacantes con el ícono de marcador para acceder rápido.'
              : activeTab === 'recomendadas'
              ? 'Agrega tus habilidades e industria en tu perfil para ver sugerencias.'
              : 'Ajusta los filtros o amplía tu búsqueda.'}
          </p>
          {activeTab !== 'todas' && (
            <button
              onClick={() => setActiveTab('todas')}
              className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-amber-400"
            >
              Ver todas las vacantes
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {displayed.map((v) => (
            <VacancyCard
              key={v._id}
              v={v}
              applied={appliedIds.includes(v._id)}
              saved={savedIds.includes(v._id)}
              recommended={recommendedIds.has(v._id)}
              onSelect={() => setSelected(v)}
              onSave={() => handleSave(v._id)}
            />
          ))}
        </div>
      )}

      {/* ── Stats bar ── */}
      {!loading && vacancies.length > 0 && (
        <div className="mt-6 flex flex-wrap items-center gap-4 rounded-xl bg-white border border-slate-200 px-5 py-3 text-xs text-slate-500">
          <span className="flex items-center gap-1.5"><Briefcase size={13} className="text-amber-500" /> <strong className="text-slate-700">{vacancies.length}</strong> vacantes disponibles</span>
          <span className="flex items-center gap-1.5"><Star size={13} className="text-amber-500" /> <strong className="text-slate-700">{recommendedIds.size}</strong> recomendadas para ti</span>
          <span className="flex items-center gap-1.5"><BookmarkCheck size={13} className="text-amber-500" /> <strong className="text-slate-700">{savedIds.length}</strong> guardadas</span>
          <span className="flex items-center gap-1.5"><Building2 size={13} className="text-amber-500" /> <strong className="text-slate-700">{appliedIds.length}</strong> postulaciones enviadas</span>
        </div>
      )}

      {/* ── Detalle de vacante ── */}
      {selected && (
        <VacancyDetail
          v={selected}
          applied={appliedIds.includes(selected._id)}
          applying={applying}
          onApply={() => handleApply(selected)}
          onClose={() => setSelected(null)}
        />
      )}
    </ColaboradorLayout>
  )
}

export default ColaboradorEmpleos
